import {
    IonBackButton,
    IonButtons,
    IonCol,
    IonLabel,
    IonRow,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { useAtom } from 'jotai';
import React, { memo, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';

import LoadableContent from '../components/content/LoadableContent';
import DocumentViewerModal, {
    DocumentNames,
} from '../components/documentviewermodal/DocumentViewerModal';
import LoadingButton from '../components/loadingbutton/LoadingButton';
import Page from '../components/page/Page';
import Signature from '../components/signature/Signature';
import { IInstallationRouteProps } from '../interfaces/IRoute';
import {
    accessTokenAtom,
    jobDetailsWithPhotosAndStateAtom,
    jobIdAtom,
} from '../store/Atoms';
import { uploadImageRequest } from '../store/Requests';
import { ImageCategories } from '../types/ImageInfo';
import { isCeeEnabled, isQuoteSigned } from '../utils/cee';
import { APP_NAME } from '../utils/constants';
import { getErrorMessage } from '../utils/errorMessage';
import { useCustomRouter } from '../utils/hooks/useCustomRouter';

const CeeQuote: React.FC<IInstallationRouteProps> = memo(() => {
    const [accessToken] = useAtom(accessTokenAtom);
    const [jobId] = useAtom(jobIdAtom);
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [quoteSignatureDataUri, setQuoteSignatureDataUri] = useState<
        string | undefined
    >(undefined);
    const [quoteOpened, setQuoteOpened] = useState<boolean>(false);
    const [value] = useAtom(jobDetailsWithPhotosAndStateAtom);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const typeInstallation =
        value.state === 'hasData'
            ? value.data.jobDetails.typeInstallation
            : '';
    const organisationalUnit =
        value.state === 'hasData'
            ? value.data.jobDetails.organisationalUnit
            : undefined;
    const photoList: string[] = useMemo(
        () => (value.state === 'hasData' ? value.data.photoList : []),
        [value]
    );
    const hasCee = isCeeEnabled(
        typeInstallation,
        organisationalUnit ? organisationalUnit[2].hasCee : false
    );
    const hasQuoteSignature = useMemo(
        () => (photoList ? isQuoteSigned(photoList) : false),
        [photoList]
    );

    const handleClick = async () => {
        if (quoteSignatureDataUri) {
            setIsLoading(true);
            try {
                await uploadImageRequest(accessToken, jobId, {
                    typepic: ImageCategories.quoteSignature,
                    typefic: 'jpg',
                    dataUri: quoteSignatureDataUri,
                });
                // We want to reset the stack to the job details page
                router.push(`/${jobId}/cee-quote`, 'none');
                // Then we want to navigate to the next page with a forward transition
                setTimeout(() => {
                    router.push(
                        `/${jobId}/installation/inspect`,
                        'forward',
                        'pop'
                    );
                }, 0);
            } catch (e: any) {
                await presentToast({
                    message: getErrorMessage(
                        e,
                        t`Oops, something went wrong while signing quote`
                    ),
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            setIsLoading(false);
        }
    };

    return (
        <Page
            title={t`Quote Signature`}
            ionButtons={
                <IonButtons slot="start">
                    <IonBackButton text={t`Back`} />
                </IonButtons>
            }
            withPadding={true}
        >
            <LoadableContent value={value}>
                {hasCee ? (
                    hasQuoteSignature ? (
                        <Redirect
                            exact
                            to={{
                                pathname: `/${jobId}/installation/inspect`,
                            }}
                        />
                    ) : (
                        <>
                            <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
                                <IonCol>
                                    <IonLabel>
                                        <Trans>Thank you</Trans>,
                                        <br />
                                        <br />
                                        <Trans>
                                            Your quote has been computed by{' '}
                                            {APP_NAME} and is ready to be
                                            signed, please read it and ask your
                                            installer should you have any
                                            questions.
                                        </Trans>
                                    </IonLabel>
                                </IonCol>
                            </IonRow>
                            <LoadingButton onClick={() => setShowModal(true)}>
                                {t`View Quote`}
                            </LoadingButton>
                            <DocumentViewerModal
                                isOpen={showModal}
                                onModalClosed={() => setShowModal(!showModal)}
                                documentName={DocumentNames.QUOTE}
                                onDocumentOpened={setQuoteOpened}
                            />
                            {quoteOpened && (
                                <>
                                    <Signature
                                        className="ion-margin-top"
                                        onChange={setQuoteSignatureDataUri}
                                        label={t`Quote Signature`}
                                    />
                                    <LoadingButton
                                        className="ion-margin-top"
                                        onClick={handleClick}
                                        disabled={!quoteSignatureDataUri}
                                        loading={isLoading}
                                    >
                                        {t`Sign Quote`}
                                    </LoadingButton>
                                </>
                            )}
                        </>
                    )
                ) : (
                    <Redirect
                        exact
                        to={{
                            pathname: '/',
                            state: {
                                from: router.routeInfo.lastPathname || '/',
                            },
                        }}
                    />
                )}
            </LoadableContent>
        </Page>
    );
});

export default CeeQuote;
